import type {
  AddressAttributes,
  VenueAttributes as GQLInput,
  VenueRoomAttributes,
} from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * `VenueAttributes` plus the one shape the venue form actually binds: a whole attachment under
 * `logo`, where the wire takes a bare `logoId`. Absorbing that here is what lets `VenueEdit` and
 * `VenueNew` hand their form values straight to the store, as `VPEventNew` does for events.
 */
export type VenueInput = GQLInput & { logo?: { id?: number } | null };

/**
 * Every `VenueAttributes` field, enumerated from the schema, and the same for the two inputs it
 * nests. A misspelling here is a compile error; an omission is not, which is what
 * `VenueInput.spec.ts`'s round-trip test guards.
 */
const GQL_FIELDS = [
  'address', 'currency', 'id', 'includeCcFee', 'includeConvenienceFee', 'includeTaxFee',
  'locale', 'logoId', 'name', 'processingFee', 'processingFeePercentage', 'replyTo',
  'rooms', 'salesTax', 'timezone', 'url',
] as const;

const ADDRESS_FIELDS = [
  '_destroy', 'city', 'country', 'id', 'postalCode', 'state', 'street1', 'street2',
] as const;

const ROOM_FIELDS = ['_destroy', 'id', 'name'] as const;

/**
 * Compile-time exhaustiveness guards. `take()` errors on a misspelled key but not on a *missing*
 * one, so without these a field added to any of the three inputs by a later codegen run would
 * silently stop persisting. This turns that into a build failure.
 */
type _AllFieldsListed<T, Listed extends PropertyKey> =
  [Exclude<keyof T, Listed>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllFieldsListed<GQLInput, (typeof GQL_FIELDS)[number]> = true;
const _allAddressFieldsListed: _AllFieldsListed<AddressAttributes, (typeof ADDRESS_FIELDS)[number]> = true;
const _allRoomFieldsListed: _AllFieldsListed<VenueRoomAttributes, (typeof ROOM_FIELDS)[number]> = true;
void _allGqlFieldsListed;
void _allAddressFieldsListed;
void _allRoomFieldsListed;

export const toGqlInput = ({ logo, ...input }: VenueInput): GQLInput => {
  // Not `??`: an explicit `logoId` of `null` means "clear the logo" and must survive the
  // attachment the form is still holding. With neither set the key is dropped altogether, which
  // is what the legacy pages' `logoId: formData.logo?.id` spread produced — Apollo serialises an
  // undefined variable away, so an unset logo leaves the stored one alone.
  const logoId = input.logoId === undefined ? logo?.id : input.logoId;
  const venue = take<GQLInput, GQLInput>({ ...input, logoId }, ...GQL_FIELDS);

  // Named `gqlInput` and not `input`: the parameter above already destructures one.
  const gqlInput: GQLInput = { ...venue };

  // The nested inputs are whitelisted too because this module is shared, not because the two
  // venue pages send extras today — `toTypedSchema(zod)` strips unknown keys before
  // `handleSubmit` sees them. A caller handing over a venue it read back would otherwise
  // forward `VenueRoom.fromGql`'s `venue` key and fail the whole mutation on an unknown
  // argument. `_destroy` stays: `VenueFields.vue` marks a removed room with it rather than
  // splicing the array. Assigned rather than spread in conditionally so an absent nested input
  // stays an absent key.
  if (venue.address) {
    gqlInput.address = take<AddressAttributes, AddressAttributes>(venue.address, ...ADDRESS_FIELDS);
  }

  if (venue.rooms) {
    gqlInput.rooms = venue.rooms.map(room => take<VenueRoomAttributes, VenueRoomAttributes>(room, ...ROOM_FIELDS));
  }

  return gqlInput;
};
